import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Slide, WorkspaceConfig } from "@shared/types";
import { isScheduledNow, nextIndex, playableSlides, playOrder } from "@shared/playlist";
import { groupKey } from "@/lib/slide-factory";
import { SlideRenderer } from "./SlideRenderer";
import { SlideErrorBoundary } from "./SlideErrorBoundary";
import { LogoOverlay } from "./LogoOverlay";

interface CrossfadePlayerProps {
  config: WorkspaceConfig;
  /** Global mute. Audio-opt-in videos still respect their own flag. */
  muted?: boolean;
  className?: string;
  /** Enable keyboard control: ←/→ slide, ↑/↓ group, space = next. */
  keyboard?: boolean;
}

/** Plays the enabled slides in a loop, crossfading between them. Images and
 *  text slides advance on a timer; non-looping videos advance when they end. */
export function CrossfadePlayer({ config, muted = true, className, keyboard = false }: CrossfadePlayerProps) {
  // Re-evaluate schedules periodically, but only force a re-render when the set
  // of currently-scheduled slides actually changes (so steady playback — e.g. a
  // video — isn't interrupted by a needless re-render every 30s).
  const [scheduleSignal, setScheduleSignal] = useState(0);
  const schedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const compute = () =>
      playableSlides(config)
        .filter((s) => isScheduledNow(s.schedule, new Date()))
        .map((s) => s.id)
        .join("|");
    schedKeyRef.current = compute();
    const id = setInterval(() => {
      const key = compute();
      if (key !== schedKeyRef.current) {
        schedKeyRef.current = key;
        setScheduleSignal((n) => n + 1);
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [config]);

  const slides = useMemo(
    () => playableSlides(config).filter((s) => isScheduledNow(s.schedule, new Date())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, scheduleSignal],
  );
  // A content signature that is stable across schedule re-checks and identical
  // config reloads — it only changes when the playable slides actually change.
  // Used so playback doesn't restart every 30s (which cut videos short).
  const slidesKey = useMemo(() => JSON.stringify(slides), [slides]);
  // Always-current slides for callbacks/effects, without making them churn.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const { crossfadeMs, shuffle } = config.settings;

  // Order of indices into `slides`. Reshuffled each full loop when enabled.
  const [order, setOrder] = useState<number[]>(() => playOrder(slides, shuffle, Date.now()));
  const [pos, setPos] = useState(0);
  // Bumped when a one-slide playlist "advances" to itself: setPos(0→0) is a
  // state no-op, so without a key change the slide never re-mounts and a
  // non-looping video freezes on its last frame forever.
  const [replayTick, setReplayTick] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rebuild order + restart ONLY when the slide content or shuffle changes
  // (not on the periodic schedule re-check or an unchanged reload).
  useEffect(() => {
    setOrder(playOrder(slidesRef.current, shuffle, Date.now()));
    setPos(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidesKey, shuffle]);

  const advance = useCallback(() => {
    if (order.length <= 1) {
      // Wrapping to the same (only) slide: re-mount it so it restarts.
      setReplayTick((t) => t + 1);
      return;
    }
    setPos((prev) => {
      const next = nextIndex(prev, order.length);
      // Reshuffle when we wrap back to the start.
      if (shuffle && next === 0 && order.length > 1) {
        setOrder(playOrder(slidesRef.current, shuffle, Date.now()));
      }
      return next;
    });
  }, [order.length, shuffle]);

  const goPrev = useCallback(() => {
    setPos((prev) => (order.length ? (prev - 1 + order.length) % order.length : 0));
  }, [order.length]);

  // Jump to the next/previous group within the current play order.
  const jumpGroupBy = useCallback(
    (dir: 1 | -1) => {
      setPos((prev) => {
        if (order.length === 0) return prev;
        const cur = slidesRef.current;
        const keyAt = (p: number) => groupKey(cur[order[p]], order[p]);
        const curKey = keyAt(prev);
        let p = prev;
        for (let step = 0; step < order.length; step++) {
          p = (p + dir + order.length) % order.length;
          if (keyAt(p) !== curKey) return p;
        }
        return prev;
      });
    },
    [order],
  );

  // Keyboard control on the running kiosk.
  useEffect(() => {
    if (!keyboard) return;
    function onKey(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowRight":
        case " ":
          e.preventDefault();
          advance();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goPrev();
          break;
        case "ArrowDown":
          e.preventDefault();
          jumpGroupBy(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          jumpGroupBy(-1);
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, advance, goPrev, jumpGroupBy]);

  const activeSlideIndex = order[pos] ?? 0;
  const activeSlide: Slide | undefined = slides[activeSlideIndex];

  // Timer-driven advance for images, text, reviews, clocks, and looping media.
  // Non-looping videos and YouTube advance when they actually end; YouTube also
  // gets a long safety timeout so a broken embed can never freeze the loop.
  // Re-arms only when the active slide changes (not on the schedule re-check),
  // so a video plays its full length.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const cur = slidesRef.current[order[pos] ?? 0];
    if (!cur) return;

    const playsToEnd =
      (cur.type === "video" || cur.type === "youtube" || cur.type === "vimeo") && !cur.loop;
    if (playsToEnd) {
      // These advance on their real "ended" event (SlideRenderer also skips
      // ones that never start). Arm a generous safety timeout (longer than any
      // clinic clip) as the final backstop so nothing can freeze the playlist.
      timer.current = setTimeout(advance, 30 * 60 * 1000);
      return () => {
        if (timer.current) clearTimeout(timer.current);
      };
    }

    timer.current = setTimeout(advance, Math.max(1000, cur.durationSec * 1000));
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, order, slidesKey, advance, replayTick]);

  // Only keep a small window of slides mounted (previous, current, next) so a
  // large playlist doesn't decode every image/video into memory at once — that
  // was crashing kiosk PCs with "out of memory". Neighbours are preloaded for a
  // smooth crossfade; everything else is unmounted. Must stay above the
  // empty-playlist return: hooks have to run on every render.
  const renderPositions = useMemo(() => {
    const len = order.length;
    if (len === 0) return [];
    const set = new Set<number>();
    for (let d = -1; d <= 1; d++) set.add(((pos + d) % len + len) % len);
    return [...set];
  }, [order.length, pos]);

  if (slides.length === 0) {
    return (
      <div className={className} style={{ width: "100%", height: "100%" }}>
        <div className="flex h-full w-full items-center justify-center bg-black text-white/40">
          No slides configured.
        </div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ position: "relative", width: "100%", height: "100%", background: "black" }}
    >
      {renderPositions.map((p) => {
        const slide = slides[order[p]];
        if (!slide) return null;
        const isActive = p === pos;
        return (
          <div
            key={order.length <= 1 ? `${slide.id}:${replayTick}` : slide.id}
            aria-hidden={!isActive}
            style={{
              position: "absolute",
              inset: 0,
              opacity: isActive ? 1 : 0,
              transition: `opacity ${crossfadeMs}ms ease-in-out`,
              pointerEvents: "none",
            }}
          >
            <SlideErrorBoundary active={isActive} onSkip={isActive ? advance : undefined}>
              <SlideRenderer
                slide={slide}
                active={isActive}
                muted={muted}
                onVideoEnded={isActive ? advance : undefined}
              />
            </SlideErrorBoundary>
          </div>
        );
      })}
      <LogoOverlay branding={config.settings.branding} hidden={!!activeSlide?.hideLogo} />
    </div>
  );
}
